import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingFallback } from '@/components/LoadingFallback';
import { ArrowLeft, Bug, Lightbulb, TrendingUp, AlertCircle, Search, ExternalLink, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Feedback {
  id: string;
  profile_id: string;
  feedback_type: 'bug' | 'feature' | 'improvement' | 'other';
  title: string;
  description: string;
  page_url: string;
  screenshot_url?: string;
  status?: string;
  created_at: string;
  profiles?: {
    display_name: string | null;
    email: string | null;
    account_type: string | null;
  } | null;
}

const AdminFeedbackPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const { loading: authLoading } = useSupabaseAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    // Wait for both auth and role loading to complete
    if (authLoading || roleLoading) {
      return;
    }

    // Only redirect after we're certain they're not admin
    if (!isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }

    // Fetch feedback only when we know user is admin
    fetchFeedback();
  }, [currentUser, isAdmin, roleLoading, authLoading, navigate]);

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select(`
          *,
          profiles (
            display_name,
            email,
            account_type
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFeedback((data || []) as Feedback[]);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return <Bug className="h-4 w-4" />;
      case 'feature':
        return <Lightbulb className="h-4 w-4" />;
      case 'improvement':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug':
        return 'destructive';
      case 'feature':
        return 'default';
      case 'improvement':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const handleResolve = async (feedbackId: string) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ 
          status: 'resolved',
          updated_at: new Date().toISOString()
        })
        .eq('id', feedbackId);

      if (error) throw error;
      
      toast.success('Feedback marked as resolved');
      fetchFeedback();
    } catch (error) {
      console.error('Error resolving feedback:', error);
      toast.error('Failed to resolve feedback');
    }
  };

  const filteredFeedback = feedback.filter((item) => {
    const q = searchTerm.trim().toLowerCase();
    const author = item.profiles?.display_name?.toLowerCase() ?? '';
    const matchesSearch = 
      item.title.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q) ||
      author.includes(q);
    
    const matchesType = typeFilter === 'all' || item.feedback_type === typeFilter;

    return matchesSearch && matchesType;
  });

  const activeFeedback = filteredFeedback.filter(f => f.status !== 'resolved');
  const resolvedFeedback = filteredFeedback.filter(f => f.status === 'resolved');

  const feedbackByType = {
    bug: activeFeedback.filter((f) => f.feedback_type === 'bug'),
    feature: activeFeedback.filter((f) => f.feedback_type === 'feature'),
    improvement: activeFeedback.filter((f) => f.feedback_type === 'improvement'),
    other: activeFeedback.filter((f) => f.feedback_type === 'other'),
  };

  if (loading || roleLoading || authLoading) {
    return <LoadingFallback />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Feedback Dashboard</h1>
            <p className="text-muted-foreground">Beta user feedback and bug reports</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeFeedback.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Bug Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {feedbackByType.bug.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Feature Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {feedbackByType.feature.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Improvements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-secondary">
              {feedbackByType.improvement.length}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search feedback..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="bug">Bugs</SelectItem>
            <SelectItem value="feature">Features</SelectItem>
            <SelectItem value="improvement">Improvements</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">
            All ({filteredFeedback.length})
          </TabsTrigger>
          <TabsTrigger value="bug">
            Bugs ({feedbackByType.bug.length})
          </TabsTrigger>
          <TabsTrigger value="feature">
            Features ({feedbackByType.feature.length})
          </TabsTrigger>
          <TabsTrigger value="improvement">
            Improvements ({feedbackByType.improvement.length})
          </TabsTrigger>
          <TabsTrigger value="resolved">
            <CheckCircle className="h-4 w-4 mr-2" />
            Resolved ({resolvedFeedback.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Showing active feedback ({activeFeedback.length} total)
            </p>
          </div>
          {activeFeedback.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={getTypeColor(item.feedback_type) as any}>
                        <span className="mr-1">{getTypeIcon(item.feedback_type)}</span>
                        {item.feedback_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <CardTitle>{item.title}</CardTitle>
                    <CardDescription>
                      From: {item.profiles?.display_name ?? 'Unknown user'} ({item.profiles?.email ?? 'N/A'}) • 
                      {' '}{item.profiles?.account_type ?? 'N/A'}
                    </CardDescription>
                  </div>
                </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-4">
                      <p className="text-sm whitespace-pre-wrap">{item.description}</p>
                      {item.page_url && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ExternalLink className="h-3 w-3" />
                          <a 
                            href={item.page_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {item.page_url}
                          </a>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResolve(item.id)}
                      className="shrink-0"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Resolve
                    </Button>
                  </div>
                </CardContent>
            </Card>
              ))}
              {activeFeedback.length === 0 && (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No active feedback found
                  </CardContent>
                </Card>
              )}
            </TabsContent>

        {['bug', 'feature', 'improvement'].map((type) => (
          <TabsContent key={type} value={type} className="space-y-4">
            {feedbackByType[type as keyof typeof feedbackByType].map((item) => (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <CardTitle>{item.title}</CardTitle>
                      <CardDescription>
                        From: {item.profiles?.display_name ?? 'Unknown user'} ({item.profiles?.email ?? 'N/A'}) • 
                        {' '}{item.profiles?.account_type ?? 'N/A'}
                      </CardDescription>
                    </div>
                  </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-4">
                        <p className="text-sm whitespace-pre-wrap">{item.description}</p>
                        {item.page_url && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <ExternalLink className="h-3 w-3" />
                            <a 
                              href={item.page_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {item.page_url}
                            </a>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(item.id)}
                        className="shrink-0"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Resolve
                      </Button>
                    </div>
                  </CardContent>
              </Card>
            ))}
            {feedbackByType[type as keyof typeof feedbackByType].length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No {type} feedback found
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}

        <TabsContent value="resolved" className="space-y-4">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Feedback marked as resolved ({resolvedFeedback.length} total)
            </p>
          </div>
          {resolvedFeedback.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={getTypeColor(item.feedback_type) as any}>
                        <span className="mr-1">{getTypeIcon(item.feedback_type)}</span>
                        {item.feedback_type}
                      </Badge>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Resolved
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <CardTitle>{item.title}</CardTitle>
                    <CardDescription>
                      From: {item.profiles?.display_name ?? 'Unknown user'} ({item.profiles?.email ?? 'N/A'}) • 
                      {' '}{item.profiles?.account_type ?? 'N/A'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm whitespace-pre-wrap">{item.description}</p>
                {item.page_url && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ExternalLink className="h-3 w-3" />
                    <a 
                      href={item.page_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {item.page_url}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {resolvedFeedback.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No resolved feedback yet
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminFeedbackPage;
